import { useEffect, useMemo, useRef, useState } from "react";
import { useSophie } from "../context/SophieContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChatCircleDots, X, PaperPlaneTilt, ArrowClockwise, Sparkle } from "@phosphor-icons/react";

/** Filter raw history → only renderable messages. We hide tool messages and
 *  assistant messages whose only content was tool_calls (no text). */
function visibleMessages(messages) {
  return messages.filter((m) => {
    if (m.role === "tool") return false;
    if (m.role === "assistant") {
      const hasText = typeof m.content === "string" && m.content.trim().length > 0;
      return hasText;
    }
    return m.role === "user";
  });
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-fade-in-up`}
      data-testid={`sophie-msg-${role}`}
    >
      <div
        className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-secondary text-foreground border border-border"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function PendingDots() {
  return (
    <div className="flex justify-start" data-testid="sophie-thinking">
      <div className="bg-secondary border border-border rounded-md px-3.5 py-2.5">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-pulse" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-pulse" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-pulse" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

const QUICK_PROMPTS = [
  "Book a haircut for tomorrow afternoon",
  "What's available with Amelia on Friday?",
  "Cancel a booking",
  "Lookup history by phone number",
];

export default function SophiePanel() {
  const { messages, loading, open, setOpen, sendMessage, reset } = useSophie();
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const visible = useMemo(() => visibleMessages(messages), [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible.length, loading]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = input;
    setInput("");
    await sendMessage(text);
  };

  return (
    <>
      {/* Launch button — visible when panel closed */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="sophie-launch-btn"
          className="fixed bottom-20 right-6 z-40 flex items-center gap-2 px-4 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-[#2B4F39] transition-all duration-200 hover:-translate-y-0.5"
          aria-label="Open Sophie"
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="font-medium">Sophie</span>
        </button>
      )}

      {/* Mobile backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-foreground/30 backdrop-blur-[2px]"
          onClick={() => setOpen(false)}
          data-testid="sophie-backdrop"
        />
      )}

      {/* Panel */}
      <aside
        className={`fixed z-50 transition-transform duration-300 ease-out flex flex-col bg-white border-border shadow-xl
          ${open ? "translate-x-0 translate-y-0" : "translate-x-full md:translate-x-full"}
          inset-x-0 bottom-0 top-auto md:inset-y-0 md:right-0 md:left-auto md:top-0
          md:w-[420px] md:border-l h-[85vh] md:h-full rounded-t-lg md:rounded-none border-t md:border-t-0`}
        data-testid="sophie-panel"
        aria-hidden={!open}
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Sparkle size={16} weight="duotone" />
            </div>
            <div>
              <div className="font-heading text-base leading-none tracking-tight">Sophie</div>
              <div className="text-[10px] text-muted-foreground tracking-[0.2em] uppercase mt-1">Receptionist · Internal</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { reset(); }}
              data-testid="sophie-reset-btn"
              aria-label="Reset conversation"
              title="Reset conversation"
            >
              <ArrowClockwise size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(false)}
              data-testid="sophie-close-btn"
              aria-label="Close panel"
            >
              <X size={16} />
            </Button>
          </div>
        </header>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3 bg-[#F9F8F6]"
          data-testid="sophie-messages"
        >
          {visible.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} />
          ))}
          {loading && <PendingDots />}

          {visible.length <= 1 && !loading && (
            <div className="pt-4">
              <div className="overline mb-2">Try</div>
              <div className="flex flex-col gap-1.5">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => sendMessage(p)}
                    data-testid={`sophie-quick-${p.slice(0,12).toLowerCase().replace(/\s+/g,'-')}`}
                    className="text-left text-xs px-3 py-2 rounded-md bg-white border border-border hover:border-primary transition-all"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={submit} className="border-t border-border p-3 md:pb-12 bg-white" data-testid="sophie-composer">
          <div className="flex items-end gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={loading ? "Sophie is thinking..." : "Ask Sophie..."}
              disabled={loading}
              data-testid="sophie-input"
              className="bg-[#F9F8F6] flex-1"
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="sophie-send-btn"
              className="bg-primary hover:bg-[#2B4F39] h-10 w-10 p-0 shrink-0"
              aria-label="Send"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">
            Sophie uses your account permissions and the salon's booking rules.
          </div>
        </form>
      </aside>
    </>
  );
}
